/**
 * Calendar — provider-agnostic view of every scheduled / published / failed
 * post, sourced from whichever SocialScheduler is currently connected
 * (Buffer today; Hootsuite / Make.com / metricool tomorrow). Three views:
 *
 *   - Schedule: a hero header showing today's date + posts-today
 *     count + countdown to the next post, then a day-grouped feed on the
 *     left with a MiniMonth sidebar on the right.
 *   - Week: Apple Calendar–style hours × days grid (7 AM → 8 PM).
 *   - Month (default): a refined Sun → Sat grid with stacked post chips
 *     per cell, drag-to-reschedule preserved.
 *
 * Polls /api/scheduling/posts hourly while mounted (skipped when the tab
 * is hidden) so freshly scheduled jobs show up without a manual refresh.
 *
 * NO Buffer-specific UI — every label comes from the provider-agnostic
 * `ScheduledPost` shape. The "Open in scheduler" deeplink is whatever the
 * scheduler puts in `providerUrl` (Buffer's publish URL today).
 */
import {
  AlertCircle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  Columns3,
  ExternalLink,
  Image as ImageIcon,
  List as ListIcon,
  Loader2,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ScheduledPost,
  type ScheduledPostStatus,
  type SchedulingChannel,
  type SchedulingStatus,
  getSchedulingStatus,
  listSchedulingChannels,
  listSchedulingPosts,
  reschedulePost as apiReschedulePost,
  deleteScheduledPost as apiDeletePost,
} from "../api";
import { useIsMobile } from "../lib/useIsMobile";
import { PageHeader } from "../components/PageHeader";
import { RescheduleEditor } from "../components/RescheduleEditor";
import { ServiceIcon, serviceBrandBg, serviceLabel } from "../components/SocialIcons";

type ViewMode = "schedule" | "week" | "month";

const STATUS_META: Record<
  ScheduledPostStatus,
  { label: string; color: string; bg: string; border: string }
> = {
  scheduled: {
    label: "Scheduled",
    color: "#FFD500",
    bg: "rgba(255,213,0,.12)",
    border: "rgba(255,213,0,.30)",
  },
  published: {
    label: "Published",
    color: "#4ADE80",
    bg: "rgba(34,197,94,.14)",
    border: "rgba(74,222,128,.30)",
  },
  failed: {
    label: "Failed",
    color: "#F87171",
    bg: "rgba(248,113,113,.14)",
    border: "rgba(248,113,113,.30)",
  },
  draft: {
    label: "Draft",
    color: "#9CA3AF",
    bg: "rgba(156,163,175,.14)",
    border: "rgba(156,163,175,.30)",
  },
};

const ALL_STATUSES: ScheduledPostStatus[] = ["scheduled", "published", "failed"];

// ---------- helpers --------------------------------------------------------

function startOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}

function addDays(d: Date, days: number): Date {
  const n = new Date(d);
  n.setDate(n.getDate() + days);
  return n;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** "9:00 AM" → { time: "9:00", ampm: "AM" } for the two-line ScheduleRow
 *  time column. Locale-aware (uses the same formatter as fmtTime). */
function splitTime12(d: Date): { time: string; ampm: string } {
  const s = fmtTime(d);
  const m = /^(.*?)\s*([AP]M)\s*$/i.exec(s);
  if (!m) return { time: s, ampm: "" };
  return { time: m[1]!.trim(), ampm: m[2]!.toUpperCase() };
}

/** "9 AM" / "9:30 PM" → "9a" / "9:30p" for compact chips. Drops :00. */
function fmtCompactTime(d: Date): string {
  const { time, ampm } = splitTime12(d);
  const trimmed = time.replace(/:00$/, "");
  return `${trimmed}${ampm.toLowerCase().charAt(0)}`;
}

function fmtDateLong(d: Date): string {
  return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

/** Render a duration in ms as the largest reasonable unit — "45 sec",
 *  "5 min", "2 hr". Used for the "cached N ago" stale banner. */
function fmtAge(ms: number): string {
  if (ms < 60_000) return `${Math.max(1, Math.round(ms / 1000))} sec`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} hr`;
  return `${Math.round(ms / 86_400_000)} d`;
}

function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

/** Best-effort timestamp for sorting / placement. `sentAt` for published,
 *  `dueAt` otherwise. Returns null for posts the provider hasn't dated. */
function eventTime(p: ScheduledPost): Date | null {
  const ts = p.sentAt ?? p.dueAt;
  if (!ts) return null;
  const t = Date.parse(ts);
  return Number.isFinite(t) ? new Date(t) : null;
}

/**
 * The set of still-deletable posts scheduled together with `post` — one
 * video fanned out across channels at the same time. Used by the drawer's
 * "Delete all N" action so a multi-channel batch clears in one go.
 *
 * The batch is anchored on the shared MEDIA ASSET, not the timestamp. `dueAt`
 * is only minute-precision (the schedule picker zeroes seconds), so "same
 * instant" is ordinary — the timestamp does NOT uniquely identify a batch.
 * What actually keeps an unrelated post out is the media URL: two different
 * videos never share an R2 asset URL, so `(same dueAt AND same media URL)`
 * only ever groups posts of the SAME video. When a post has no detectable
 * media URL to anchor on, we deliberately DON'T guess by caption text — that
 * could merge two unrelated media-less posts — we treat it as solo so the
 * operator deletes it individually. Published posts are excluded (the provider
 * only deletes from queue). Always includes `post`; returns [post] alone when
 * it isn't deletable or has no media anchor. The drawer's confirm step also
 * shows the resulting count, so an unexpected N is visible before committing.
 */
function deletableBatch(post: ScheduledPost, all: ScheduledPost[]): ScheduledPost[] {
  if (post.status !== "scheduled" && post.status !== "failed") return [post];
  if (!post.dueAt) return [post];
  const key = post.videoUrl || post.imageUrl || null;
  if (!key) return [post];
  return all.filter((p) => {
    if (p.id === post.id) return true;
    if (p.status !== "scheduled" && p.status !== "failed") return false;
    if (p.dueAt !== post.dueAt) return false;
    return (p.videoUrl || p.imageUrl || null) === key;
  });
}

// ---------- top-level screen ----------------------------------------------

export function Calendar(): React.JSX.Element {
  // Default to Month — operator asked for the monthly grid on first open
  // (2026-07-09); the Schedule feed and Week are one click away.
  const [view, setView] = useState<ViewMode>("month");
  const [status, setStatus] = useState<SchedulingStatus | null>(null);
  const [channels, setChannels] = useState<SchedulingChannel[]>([]);
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [activeChannelIds, setActiveChannelIds] = useState<string[]>([]);
  const [activeStatuses, setActiveStatuses] = useState<ScheduledPostStatus[]>(ALL_STATUSES);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Rate-limit banner state — server returned cached posts with a
  // `stale: true` flag because the provider throttled us. We keep the
  // calendar populated and just surface a warning rather than wiping it.
  const [stale, setStale] = useState<{
    ageMs: number;
    window: string | null;
  } | null>(null);
  const [selected, setSelected] = useState<ScheduledPost | null>(null);
  // Anchor for Week + Month + MiniMonth navigation. The List/Schedule view
  // uses the server-default window so the anchor is only consulted for
  // mini-month navigation in that case.
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));
  // Drag-to-reschedule: ids currently in-flight (PATCH not yet acked).
  // Used to dim the chip and ignore concurrent drags on the same post.
  const [rescheduling, setRescheduling] = useState<Set<string>>(new Set());
  // One-shot success toast — clears itself after a couple seconds. Lives
  // here (not as part of the chip) so the user sees the confirmation even
  // if they immediately drop the post onto another day.
  const [toast, setToast] = useState<string | null>(null);

  // Drag-and-drop reschedule. Keeps the post's time of day, replaces
  // only the calendar day. Optimistic — the UI moves immediately and
  // reverts if the server rejects.
  const handleReschedule = useCallback(
    async (postId: string, targetDate: Date): Promise<void> => {
      setPosts((prev) => {
        const idx = prev.findIndex((p) => p.id === postId);
        if (idx === -1) return prev;
        const post = prev[idx]!;
        if (!post.dueAt) return prev;
        const cur = new Date(post.dueAt);
        const next = new Date(targetDate);
        next.setHours(cur.getHours(), cur.getMinutes(), cur.getSeconds(), 0);
        // Same-day drop is a no-op — bail early so we don't burn a PATCH.
        if (next.getTime() === cur.getTime()) return prev;
        const nextIso = next.toISOString();
        // Fire the API call, capturing the previous dueAt so we can roll
        // back the optimistic move if Buffer rejects.
        const previousDueAt = post.dueAt;
        setRescheduling((s) => new Set(s).add(postId));
        void apiReschedulePost(postId, nextIso)
          .then((updated) => {
            setPosts((curPosts) =>
              curPosts.map((p) => (p.id === postId ? { ...p, ...updated } : p)),
            );
            setToast(`Moved to ${next.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`);
            window.setTimeout(() => setToast(null), 2500);
          })
          .catch((e: unknown) => {
            // Rollback to the prior dueAt; surface the error to the user.
            setPosts((curPosts) =>
              curPosts.map((p) => (p.id === postId ? { ...p, dueAt: previousDueAt } : p)),
            );
            setError(e instanceof Error ? e.message : String(e));
          })
          .finally(() => {
            setRescheduling((s) => {
              const n = new Set(s);
              n.delete(postId);
              return n;
            });
          });
        const optimistic = [...prev];
        optimistic[idx] = { ...post, dueAt: nextIso };
        return optimistic;
      });
    },
    [],
  );

  // Reschedule a single post to an EXACT new instant (date + time), used by the
  // drawer's inline time editor. Same optimistic-update + rollback contract as
  // the drag path, but returns a Promise so the editor can close on success and
  // surface the error on failure.
  const rescheduleToISO = useCallback(
    (postId: string, nextIso: string): Promise<void> =>
      new Promise((resolve, reject) => {
        setPosts((prev) => {
          const idx = prev.findIndex((p) => p.id === postId);
          if (idx === -1) {
            resolve();
            return prev;
          }
          const post = prev[idx]!;
          const previousDueAt = post.dueAt;
          setRescheduling((s) => new Set(s).add(postId));
          void apiReschedulePost(postId, nextIso)
            .then((updated) => {
              setPosts((cur) => cur.map((p) => (p.id === postId ? { ...p, ...updated } : p)));
              setToast("Rescheduled");
              window.setTimeout(() => setToast(null), 2500);
              resolve();
            })
            .catch((e: unknown) => {
              setPosts((cur) => cur.map((p) => (p.id === postId ? { ...p, dueAt: previousDueAt } : p)));
              reject(e instanceof Error ? e : new Error(String(e)));
            })
            .finally(() => {
              setRescheduling((s) => {
                const n = new Set(s);
                n.delete(postId);
                return n;
              });
            });
          const optimistic = [...prev];
          optimistic[idx] = { ...post, dueAt: nextIso };
          return optimistic;
        });
      }),
    [],
  );

  // ---- data load --------------------------------------------------------

  const lastFetchRef = useRef<number>(0);
  const fetchPosts = useCallback(
    async (opts?: { force?: boolean }) => {
      // Manual-refresh throttle: 10s between live fetches. Without this
      // the operator can spam the ↻ button and burn through Buffer's
      // daily quota during testing. The server has its own 60s cache
      // anyway — extra clicks are no-ops.
      const since = Date.now() - lastFetchRef.current;
      if (!opts?.force && since < 10_000) return;
      lastFetchRef.current = Date.now();

      setRefreshing(true);
      setError(null);
      try {
        // For Month + Week views we pull a wider window so neighbouring
        // periods have data when the user navigates. Schedule view uses
        // the server-default window (-7d → +60d).
        const monthFromTo =
          view === "month"
            ? {
                from: addDays(startOfDay(new Date(anchor.getFullYear(), anchor.getMonth(), 1)), -7).toISOString(),
                to: addDays(startOfDay(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1)), 7).toISOString(),
              }
            : view === "week"
              ? {
                  from: addDays(startOfDay(addDays(anchor, -anchor.getDay())), -7).toISOString(),
                  to: addDays(startOfDay(addDays(anchor, 7 - anchor.getDay())), 7).toISOString(),
                }
              : {};
        const resp = await listSchedulingPosts(monthFromTo);
        setPosts(resp.posts);
        if (resp.stale && resp.rateLimited) {
          setStale({ ageMs: resp.staleAgeMs ?? 0, window: resp.rateLimitWindow ?? null });
        } else {
          setStale(null);
        }
      } catch (e) {
        const err = e as Error & { rateLimited?: boolean };
        // 429 with no cache: don't clobber whatever we already had, just
        // surface a friendlier banner.
        if (err.rateLimited) {
          setStale({ ageMs: 0, window: null });
          setError(null);
        } else {
          setError(err.message ?? String(e));
        }
      } finally {
        setRefreshing(false);
        setLoaded(true);
      }
    },
    [view, anchor],
  );

  // Posts currently being deleted (DELETE in flight). Drives the drawer
  // spinner and blocks a second delete on the same post.
  const [deleting, setDeleting] = useState<Set<string>>(new Set());

  // Delete one or more posts from the provider (Buffer) AND the calendar.
  // Optimistic: the posts vanish immediately; on any failure we refetch to
  // reconcile with the provider's real state and surface the error. Runs the
  // ids in parallel (best-effort) so a multi-channel batch clears in one go.
  const handleDelete = useCallback(
    async (ids: string[]): Promise<void> => {
      if (!ids.length) return;
      const idSet = new Set(ids);
      setDeleting((s) => {
        const n = new Set(s);
        ids.forEach((i) => n.add(i));
        return n;
      });
      // Optimistically remove from the calendar.
      setPosts((prev) => prev.filter((p) => !idSet.has(p.id)));
      setError(null);
      const results = await Promise.allSettled(ids.map((id) => apiDeletePost(id)));
      setDeleting((s) => {
        const n = new Set(s);
        ids.forEach((i) => n.delete(i));
        return n;
      });
      const failed = results.filter((r): r is PromiseRejectedResult => r.status === "rejected");
      if (failed.length) {
        const reason = failed[0]!.reason;
        setError(
          `Couldn't delete ${failed.length} of ${ids.length} post${ids.length > 1 ? "s" : ""} — ${reason instanceof Error ? reason.message : String(reason)}`,
        );
        // Our optimistic removal may now disagree with the provider — pull the
        // truth back so a post that failed to delete reappears on the calendar.
        void fetchPosts({ force: true });
      } else {
        setToast(ids.length === 1 ? "Post deleted" : `${ids.length} posts deleted`);
        window.setTimeout(() => setToast(null), 2500);
      }
    },
    [fetchPosts],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const s = await getSchedulingStatus();
        if (cancelled) return;
        setStatus(s);
        if (s.connected) {
          try {
            const ch = await listSchedulingChannels();
            if (!cancelled) setChannels(ch);
          } catch {
            /* non-fatal — calendar still renders without channel chips */
          }
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!status?.connected) return;
    void fetchPosts({ force: true });
    // Hourly auto-refresh. Buffer enforces both a 60-req/min limit AND
    // an undocumented daily quota, so we keep this loose. Skip the tick
    // when the tab's hidden — no point burning quota for nobody to read.
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void fetchPosts();
    }, 60 * 60 * 1000);
    return () => window.clearInterval(id);
  }, [status?.connected, fetchPosts]);

  // ---- filtering --------------------------------------------------------

  const filtered = useMemo(() => {
    return posts.filter((p) => {
      if (activeChannelIds.length && !activeChannelIds.includes(p.channelId)) return false;
      if (!activeStatuses.includes(p.status)) return false;
      return true;
    });
  }, [posts, activeChannelIds, activeStatuses]);

  // Today's posts regardless of filter — drives the hero stat. Sorted by
  // event time ascending so the channel-mix pills read in time order.
  const todaysPosts = useMemo(() => {
    const today = startOfDay(new Date());
    return posts
      .filter((p) => {
        const t = eventTime(p);
        return t && isSameDay(t, today);
      })
      .sort((a, b) => (eventTime(a)!.getTime() - eventTime(b)!.getTime()));
  }, [posts]);

  // Next scheduled post strictly in the future — drives the countdown
  // pill. Filtered to "scheduled" so a recently-published post doesn't
  // suddenly look like the "next" one.
  const nextScheduled = useMemo(() => {
    const now = Date.now();
    const candidates = posts
      .filter((p) => p.status === "scheduled")
      .map((p) => ({ post: p, t: eventTime(p) }))
      .filter((x): x is { post: ScheduledPost; t: Date } => !!x.t && x.t.getTime() > now)
      .sort((a, b) => a.t.getTime() - b.t.getTime());
    return candidates[0] ?? null;
  }, [posts]);

  // ---- empty states -----------------------------------------------------

  if (status && !status.connected) {
    return (
      <div>
        <PageHeader
          eyebrow="Calendar"
          title="Connect a scheduler to see your posts here."
          subtitle="The calendar reads from whichever SocialScheduler is paired in Settings. Connect Buffer (or your scheduler of choice) first."
        />
        <EmptyHero
          icon={<CalendarDays size={28} strokeWidth={1.5} />}
          title="No scheduler connected"
          body="Open Settings → Scheduling and paste a Buffer access token. Once connected, every queued and published post will show up on this calendar."
        />
      </div>
    );
  }

  return (
    <div>
      <CalendarHeroHeader
        todaysPosts={todaysPosts}
        nextScheduled={nextScheduled}
        view={view}
        onView={setView}
        refreshing={refreshing}
        onRefresh={() => {
          void fetchPosts();
        }}
        provider={status?.provider}
        accountName={status?.accountName}
      />

      <FilterBar
        channels={channels}
        activeChannelIds={activeChannelIds}
        setActiveChannelIds={setActiveChannelIds}
        activeStatuses={activeStatuses}
        setActiveStatuses={setActiveStatuses}
      />

      {stale && (
        <div
          role="status"
          style={{
            margin: "0 0 18px",
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(255,213,0,.06)",
            border: "1px solid rgba(255,213,0,.25)",
            color: "var(--ql-yellow)",
            fontSize: 12,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <RefreshCw size={13} strokeWidth={2} />
          <span>
            Buffer is throttling us
            {stale.window ? ` (${stale.window} window)` : ""}.{" "}
            {stale.ageMs > 0
              ? `Showing cached data from ${fmtAge(stale.ageMs)} ago.`
              : "No fresh data could be loaded — try again in a few minutes."}
          </span>
        </div>
      )}

      {error && (
        <div
          role="alert"
          style={{
            margin: "0 0 18px",
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(248,113,113,.10)",
            border: "1px solid rgba(248,113,113,.30)",
            color: "#FCA5A5",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {!loaded ? (
        <SkeletonRows />
      ) : view === "schedule" ? (
        <ScheduleView posts={filtered} onOpen={setSelected} anchor={anchor} onAnchor={setAnchor} />
      ) : view === "week" ? (
        <WeekView anchor={anchor} onChangeAnchor={setAnchor} posts={filtered} onOpen={setSelected} />
      ) : (
        <MonthView
          anchor={anchor}
          onChangeAnchor={setAnchor}
          posts={filtered}
          onOpen={setSelected}
          onReschedule={handleReschedule}
          rescheduling={rescheduling}
        />
      )}

      {selected && (
        <PostDrawer
          post={selected}
          batch={deletableBatch(selected, posts)}
          deleting={deleting.has(selected.id)}
          onDelete={async (ids) => {
            await handleDelete(ids);
            setSelected(null);
          }}
          onClose={() => setSelected(null)}
          onReschedule={rescheduleToISO}
        />
      )}

      {toast && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 18px",
            borderRadius: 999,
            background: "rgba(74,222,128,.14)",
            border: "1px solid rgba(74,222,128,.30)",
            color: "#4ADE80",
            fontSize: 12,
            fontWeight: 700,
            backdropFilter: "blur(10px)",
            zIndex: 100,
            animation: "ql-fade-up .25s var(--ql-ease-soft)",
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

// ---------- hero header ----------------------------------------------------

function CalendarHeroHeader({
  todaysPosts,
  nextScheduled,
  view,
  onView,
  refreshing,
  onRefresh,
  provider,
  accountName,
}: {
  todaysPosts: ScheduledPost[];
  nextScheduled: { post: ScheduledPost; t: Date } | null;
  view: ViewMode;
  onView: (v: ViewMode) => void;
  refreshing: boolean;
  onRefresh: () => void;
  provider: string | undefined;
  accountName: string | null | undefined;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const today = new Date();
  const dateLong = today.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Aggregate channel-mix for the hero stat. Object preserves insertion
  // order so the pills mirror the chronological order of todaysPosts.
  const channelMix: Record<string, number> = {};
  for (const p of todaysPosts) {
    channelMix[p.service] = (channelMix[p.service] ?? 0) + 1;
  }
  const mixEntries = Object.entries(channelMix);

  // Countdown to next scheduled post — humanised under three thresholds.
  let nextLabel: string | null = null;
  if (nextScheduled) {
    const diffMs = nextScheduled.t.getTime() - Date.now();
    const hrs = Math.floor(diffMs / 3_600_000);
    const mins = Math.floor((diffMs % 3_600_000) / 60_000);
    if (hrs >= 24) nextLabel = `in ${Math.floor(hrs / 24)}d ${hrs % 24}h`;
    else if (hrs >= 1) nextLabel = `in ${hrs}h ${mins}m`;
    else nextLabel = `in ${mins}m`;
  }

  return (
    <div style={{ marginBottom: 28 }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 24,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
              letterSpacing: ".14em",
              marginBottom: 10,
            }}
          >
            Calendar
          </div>
          <h1
            style={{
              fontSize: isMobile ? 28 : 44,
              fontWeight: 800,
              margin: 0,
              letterSpacing: "-0.035em",
              lineHeight: 1.05,
              background: "linear-gradient(180deg,#FFFFFF 0%,#B8BFCC 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {dateLong}
          </h1>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              alignItems: "center",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                fontSize: 16,
                color: "#D1D5DB",
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--ql-font-mono)",
                  fontSize: 28,
                  fontWeight: 800,
                  color: "var(--ql-yellow)",
                  letterSpacing: "-0.03em",
                }}
              >
                {todaysPosts.length}
              </span>
              <span>
                {todaysPosts.length === 1
                  ? "post going out today"
                  : "posts going out today"}
              </span>
            </div>
            {mixEntries.length > 0 && (
              <div style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
                {mixEntries.map(([svc, n]) => (
                  <span
                    key={svc}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px 4px 4px",
                      borderRadius: 999,
                      background: "rgba(255,255,255,.04)",
                      border: "1px solid rgba(255,255,255,.06)",
                    }}
                  >
                    <ServiceIcon service={svc as ScheduledPost["service"]} size={18} />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#D1D5DB",
                        fontFamily: "var(--ql-font-mono)",
                      }}
                    >
                      {n}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>

          {nextScheduled && nextLabel && (
            <div
              style={{
                marginTop: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 14px",
                borderRadius: 999,
                background: "rgba(255,213,0,.07)",
                border: "1px solid rgba(255,213,0,.18)",
              }}
            >
              <ServiceIcon service={nextScheduled.post.service} size={18} />
              <span style={{ fontSize: 12, color: "var(--ql-fg-muted)" }}>Next post</span>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: "var(--ql-yellow)",
                  fontFamily: "var(--ql-font-mono)",
                }}
              >
                {nextLabel}
              </span>
              <span
                style={{
                  fontSize: 12,
                  color: "#D1D5DB",
                  fontWeight: 500,
                  maxWidth: 280,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={nextScheduled.post.text}
              >
                — {nextScheduled.post.text.split("\n")[0]}
              </span>
            </div>
          )}

          {(provider || accountName) && (
            <div
              style={{
                marginTop: 10,
                fontSize: 11,
                color: "var(--ql-fg-subtle)",
                fontFamily: "var(--ql-font-mono)",
              }}
            >
              {provider
                ? `Reading from ${provider.charAt(0).toUpperCase() + provider.slice(1)}`
                : ""}
              {accountName ? ` · ${accountName}` : ""}
            </div>
          )}
        </div>

        <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
          <ViewSegment view={view} onView={onView} />
          <button
            type="button"
            onClick={onRefresh}
            title="Refresh now"
            aria-label="Refresh"
            style={iconBtn}
          >
            <RefreshCw
              size={14}
              strokeWidth={2}
              className={refreshing ? "ql-spin" : undefined}
            />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- view segment ---------------------------------------------------

function ViewSegment({
  view,
  onView,
}: {
  view: ViewMode;
  onView: (v: ViewMode) => void;
}): React.JSX.Element {
  const opts: Array<{ id: ViewMode; label: string; Icon: typeof ListIcon }> = [
    { id: "schedule", label: "Schedule", Icon: ListIcon },
    { id: "week", label: "Week", Icon: Columns3 },
    { id: "month", label: "Month", Icon: CalendarDays },
  ];
  return (
    <div
      role="tablist"
      aria-label="Calendar view"
      style={{
        display: "inline-flex",
        padding: 4,
        borderRadius: 14,
        background: "rgba(255,255,255,.03)",
        border: "1px solid rgba(255,255,255,.06)",
        boxShadow:
          "0 1px 3px rgba(0,0,0,.20), inset 0 1px 0 rgba(255,255,255,.03)",
      }}
    >
      {opts.map((o) => {
        const isActive = view === o.id;
        const Icon = o.Icon;
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onView(o.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "8px 14px",
              borderRadius: 10,
              border: "none",
              background: isActive
                ? "linear-gradient(180deg, rgba(255,213,0,.18), rgba(255,165,0,.10))"
                : "transparent",
              color: isActive ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: isActive
                ? "0 1px 0 rgba(255,255,255,.06) inset, 0 4px 12px rgba(255,213,0,.15)"
                : "none",
              transition: "all .2s var(--ql-ease-soft)",
            }}
          >
            <Icon size={13} strokeWidth={2} />
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------- filter bar -----------------------------------------------------

function FilterBar({
  channels,
  activeChannelIds,
  setActiveChannelIds,
  activeStatuses,
  setActiveStatuses,
}: {
  channels: SchedulingChannel[];
  activeChannelIds: string[];
  setActiveChannelIds: (ids: string[]) => void;
  activeStatuses: ScheduledPostStatus[];
  setActiveStatuses: (s: ScheduledPostStatus[]) => void;
}): React.JSX.Element {
  const toggleChannel = (id: string): void => {
    setActiveChannelIds(
      activeChannelIds.includes(id)
        ? activeChannelIds.filter((c) => c !== id)
        : [...activeChannelIds, id],
    );
  };
  const toggleStatus = (s: ScheduledPostStatus): void => {
    setActiveStatuses(
      activeStatuses.includes(s)
        ? activeStatuses.filter((x) => x !== s)
        : [...activeStatuses, s],
    );
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        marginBottom: 26,
        alignItems: "center",
        flexWrap: "wrap",
        padding: "10px 12px",
        borderRadius: 14,
        background: "rgba(255,255,255,.025)",
        border: "1px solid rgba(255,255,255,.05)",
        backdropFilter: "blur(20px)",
      }}
    >
      <div style={{ display: "inline-flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
        {channels.map((c) => {
          const isOn = activeChannelIds.length === 0 || activeChannelIds.includes(c.id);
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => toggleChannel(c.id)}
              title={`${serviceLabel(c.service)} · ${c.name}`}
              aria-pressed={isOn}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 11px 5px 5px",
                borderRadius: 999,
                border: isOn
                  ? "1px solid rgba(255,255,255,.10)"
                  : "1px solid transparent",
                background: isOn ? "rgba(255,255,255,.04)" : "transparent",
                color: isOn ? "var(--ql-fg)" : "var(--ql-fg-muted)",
                opacity: activeChannelIds.length === 0 || isOn ? 1 : 0.4,
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all .15s var(--ql-ease)",
              }}
            >
              <ServiceIcon service={c.service} size={20} />
              <span
                style={{
                  maxWidth: 110,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.name}
              </span>
            </button>
          );
        })}
        {activeChannelIds.length > 0 && (
          <button
            type="button"
            onClick={() => setActiveChannelIds([])}
            style={{
              fontFamily: "inherit",
              fontSize: 11,
              color: "var(--ql-fg-subtle)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "4px 6px",
            }}
          >
            Clear
          </button>
        )}
      </div>

      <span style={{ flex: 1 }} />

      <div style={{ display: "inline-flex", gap: 4 }}>
        {ALL_STATUSES.map((s) => {
          const isOn = activeStatuses.includes(s);
          const meta = STATUS_META[s];
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleStatus(s)}
              aria-pressed={isOn}
              style={{
                padding: "5px 12px",
                borderRadius: 999,
                border: "none",
                background: isOn ? meta.bg : "transparent",
                color: isOn ? meta.color : "var(--ql-fg-subtle)",
                fontFamily: "inherit",
                fontSize: 11,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                transition: "all .15s var(--ql-ease)",
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: isOn ? meta.color : "var(--ql-fg-faint)",
                }}
              />
              {meta.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const iconBtn: React.CSSProperties = {
  width: 34,
  height: 34,
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.02)",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const miniNavBtn: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,.08)",
  background: "rgba(255,255,255,.02)",
  color: "var(--ql-fg-muted)",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
  marginLeft: 4,
};

const miniLabel: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: "var(--ql-fg-subtle)",
  textTransform: "uppercase",
  letterSpacing: ".14em",
};

// ---------- schedule view --------------------------------------------------

function ScheduleView({
  posts,
  onOpen,
  anchor,
  onAnchor,
}: {
  posts: ScheduledPost[];
  onOpen: (p: ScheduledPost) => void;
  anchor: Date;
  onAnchor: (d: Date) => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  // Group by day. Sorted ascending by time within a group; days sorted with
  // today + future ascending first, then past in reverse, undated last.
  const groups = useMemo(() => groupByDay(posts), [posts]);
  const today = startOfDay(new Date());

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 280px",
        gap: 28,
        alignItems: "flex-start",
      }}
    >
      <div style={{ minWidth: 0 }}>
        {groups.length === 0 ? (
          <EmptyHero
            icon={<CalendarDays size={28} strokeWidth={1.5} />}
            title="Nothing scheduled"
            body="Schedule a post from a job's detail page (or in Buffer directly) — hit the ↻ button up top to pull it in immediately, or wait for the next hourly refresh."
          />
        ) : (
          groups.map((g) => (
            <DayGroup
              key={g.dateKey}
              date={g.date}
              posts={g.posts}
              today={today}
              onOpen={onOpen}
            />
          ))
        )}
      </div>

      <MiniMonth anchor={anchor} onAnchor={onAnchor} posts={posts} />
    </div>
  );
}

function DayGroup({
  date,
  posts,
  today,
  onOpen,
}: {
  date: Date | null;
  posts: ScheduledPost[];
  today: Date;
  onOpen: (p: ScheduledPost) => void;
}): React.JSX.Element {
  const isUndated = !date;
  const isToday = !!date && isSameDay(date, today);
  const isTomorrow = !!date && isSameDay(date, addDays(today, 1));
  const isYesterday = !!date && isSameDay(date, addDays(today, -1));
  const isPast = !!date && date.getTime() < today.getTime();

  const pillLabel = isUndated
    ? "Undated"
    : isToday
      ? "Today"
      : isTomorrow
        ? "Tomorrow"
        : isYesterday
          ? "Yesterday"
          : date.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
  const dateLabel = date ? fmtDateLong(date) : "No scheduled time";

  return (
    <div style={{ marginBottom: 32 }}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          padding: "12px 0 14px",
          background: "linear-gradient(180deg, var(--ql-bg) 65%, transparent)",
          display: "flex",
          alignItems: "baseline",
          gap: 14,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 14, flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: "var(--ql-font-mono)",
              fontSize: 38,
              fontWeight: 800,
              letterSpacing: "-0.04em",
              color: isToday
                ? "var(--ql-yellow)"
                : isPast
                  ? "var(--ql-fg-faint)"
                  : "var(--ql-fg)",
              lineHeight: 0.9,
              minWidth: 56,
            }}
          >
            {date ? date.getDate() : "—"}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: isToday ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                textTransform: "uppercase",
                letterSpacing: ".14em",
                marginBottom: 2,
              }}
            >
              {pillLabel}
            </div>
            <div
              style={{
                fontSize: 14,
                color: isPast ? "var(--ql-fg-subtle)" : "#D1D5DB",
                fontWeight: 500,
              }}
            >
              {dateLabel}
            </div>
          </div>
        </div>
        <span
          style={{
            fontSize: 11,
            color: "var(--ql-fg-subtle)",
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {posts.length} {posts.length === 1 ? "post" : "posts"}
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {posts.map((p) => (
          <ScheduleRow key={p.id} post={p} isPast={isPast} onOpen={onOpen} />
        ))}
      </div>
    </div>
  );
}

function ScheduleRow({
  post,
  isPast,
  onOpen,
}: {
  post: ScheduledPost;
  isPast: boolean;
  onOpen: (p: ScheduledPost) => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const when = eventTime(post);
  const meta = STATUS_META[post.status];
  const isFailed = post.status === "failed";
  const timeParts = when ? splitTime12(when) : null;

  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "56px 1fr auto" : "78px 64px 1fr auto",
        gap: isMobile ? 10 : 18,
        alignItems: "center",
        padding: "14px 18px",
        borderRadius: 16,
        border: isFailed
          ? "1px solid rgba(248,113,113,.20)"
          : "1px solid rgba(255,255,255,.05)",
        background: isFailed ? "rgba(248,113,113,.04)" : "rgba(255,255,255,.025)",
        color: "var(--ql-fg)",
        textAlign: "left",
        cursor: "pointer",
        transition: "all .2s var(--ql-ease-soft)",
        fontFamily: "inherit",
        opacity: isPast ? 0.66 : 1,
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = isFailed
          ? "rgba(248,113,113,.08)"
          : "rgba(255,255,255,.05)";
        e.currentTarget.style.transform = "translateX(2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isFailed
          ? "rgba(248,113,113,.04)"
          : "rgba(255,255,255,.025)";
        e.currentTarget.style.transform = "translateX(0)";
      }}
    >
      <div style={{ textAlign: "left" }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 700,
            fontFamily: "var(--ql-font-mono)",
            color: "var(--ql-fg)",
            letterSpacing: "-0.02em",
          }}
        >
          {timeParts ? timeParts.time : "—"}
        </div>
        <div
          style={{
            fontSize: 10,
            color: "var(--ql-fg-subtle)",
            fontWeight: 700,
            letterSpacing: ".10em",
            marginTop: 1,
          }}
        >
          {timeParts ? timeParts.ampm : ""}
        </div>
      </div>

      {!isMobile && <ThumbStrip post={post} />}

      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            marginBottom: 4,
          }}
        >
          <ServiceIcon service={post.service} size={16} />
          <span style={{ fontSize: 11, fontWeight: 600, color: "var(--ql-fg-muted)" }}>
            {serviceLabel(post.service)}
          </span>
          <span
            style={{
              width: 3,
              height: 3,
              borderRadius: 999,
              background: "var(--ql-fg-faint)",
            }}
          />
          <span
            style={{
              fontSize: 11,
              color: "var(--ql-fg-muted)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 200,
            }}
          >
            {post.channelName}
          </span>
        </div>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.45,
            color: "#E5E7EB",
            fontWeight: 500,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {post.text || <em style={{ color: "var(--ql-fg-subtle)" }}>(no caption)</em>}
        </div>
        {post.errorMessage && (
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: "#FCA5A5",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <AlertCircle size={11} strokeWidth={2} /> {post.errorMessage}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: meta.color,
            textTransform: "uppercase",
            letterSpacing: ".10em",
          }}
        >
          {meta.label}
        </span>
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: meta.color,
            boxShadow: `0 0 12px ${meta.color}88`,
          }}
        />
      </div>
    </button>
  );
}

/** Compact 9:16 thumb-strip used in ScheduleRow. Renders the post's
 *  imageUrl when available; otherwise a brand-tinted gradient with an
 *  Image icon — same shape PostThumb used to give us at 64×96, just
 *  trimmed to 36×64 to leave more room for the caption column. */
function ThumbStrip({ post }: { post: ScheduledPost }): React.JSX.Element {
  const cover = post.imageUrl ?? null;
  return (
    <div
      style={{
        width: 36,
        height: 64,
        borderRadius: 8,
        background: cover
          ? `center/cover no-repeat url("${cover}")`
          : "linear-gradient(135deg,#1a1f2e,var(--ql-bg))",
        border: "1px solid rgba(255,255,255,.06)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "rgba(255,213,0,.30)",
        position: "relative",
        overflow: "hidden",
      }}
      aria-hidden="true"
    >
      {!cover && <ImageIcon size={14} strokeWidth={1.5} />}
      <div
        style={{
          position: "absolute",
          left: 4,
          right: 4,
          bottom: 4,
          height: 2,
          borderRadius: 999,
          background: "rgba(255,213,0,.30)",
        }}
      />
    </div>
  );
}

// ---------- mini-month sidebar --------------------------------------------

function MiniMonth({
  anchor,
  onAnchor,
  posts,
}: {
  anchor: Date;
  onAnchor: (d: Date) => void;
  posts: ScheduledPost[];
}): React.JSX.Element {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = startOfDay(new Date());

  const byDay = useMemo(() => {
    const m = new Map<string, ScheduledPost[]>();
    for (const p of posts) {
      const t = eventTime(p);
      if (!t) continue;
      const key = startOfDay(t).toDateString();
      const arr = m.get(key) ?? [];
      arr.push(p);
      m.set(key, arr);
    }
    return m;
  }, [posts]);

  const goPrev = (): void =>
    onAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const goNext = (): void =>
    onAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));

  return (
    <div
      style={{
        padding: 20,
        borderRadius: 18,
        background: "rgba(16,18,27,.5)",
        border: "1px solid rgba(255,255,255,.06)",
        backdropFilter: "blur(20px)",
        position: "sticky",
        top: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
              letterSpacing: ".10em",
            }}
          >
            {anchor.toLocaleDateString(undefined, { year: "numeric" })}
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em" }}>
            {anchor.toLocaleDateString(undefined, { month: "long" })}
          </div>
        </div>
        <button type="button" aria-label="Previous month" onClick={goPrev} style={miniNavBtn}>
          <ChevronLeft size={13} strokeWidth={2} />
        </button>
        <button type="button" aria-label="Next month" onClick={goNext} style={miniNavBtn}>
          <ChevronRight size={13} strokeWidth={2} />
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0,1fr))",
          marginBottom: 6,
        }}
      >
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div
            key={i}
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ql-fg-subtle)",
              textAlign: "center",
              letterSpacing: ".06em",
            }}
          >
            {d}
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, minmax(0,1fr))",
          gap: 1,
        }}
      >
        {days.map((d) => {
          const isCurrentMonth = d.getMonth() === anchor.getMonth();
          const isTodayCell = isSameDay(d, today);
          const dayPosts = byDay.get(d.toDateString()) ?? [];
          const services = Array.from(new Set(dayPosts.map((p) => p.service)));
          return (
            <button
              key={d.toDateString()}
              type="button"
              onClick={() => onAnchor(d)}
              style={{
                aspectRatio: "1 / 1",
                border: "none",
                background: "transparent",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 8,
                padding: 0,
                fontFamily: "inherit",
                color: isTodayCell
                  ? "#0B0C12"
                  : !isCurrentMonth
                    ? "var(--ql-fg-faint)"
                    : "#D1D5DB",
                fontSize: 12,
                fontWeight: 600,
                position: "relative",
                transition: "all .15s var(--ql-ease)",
              }}
              onMouseEnter={(e) => {
                if (!isTodayCell) e.currentTarget.style.background = "rgba(255,255,255,.06)";
              }}
              onMouseLeave={(e) => {
                if (!isTodayCell) e.currentTarget.style.background = "transparent";
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isTodayCell ? "var(--ql-yellow)" : "transparent",
                  fontFamily: "var(--ql-font-mono)",
                  fontWeight: isTodayCell ? 800 : 600,
                }}
              >
                {d.getDate()}
              </div>
              {services.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    bottom: 3,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                  }}
                >
                  {services.slice(0, 3).map((svc) => (
                    <span
                      key={svc}
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: 999,
                        background: serviceBrandBg(svc),
                      }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: "1px solid rgba(255,255,255,.06)",
          fontSize: 10,
          color: "var(--ql-fg-subtle)",
          display: "flex",
          flexDirection: "column",
          gap: 5,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              width: 4,
              height: 4,
              borderRadius: 999,
              background: "var(--ql-yellow)",
            }}
          />
          <span>Dot per channel scheduled</span>
        </div>
      </div>
    </div>
  );
}

// ---------- week view ------------------------------------------------------

function WeekView({
  anchor,
  onChangeAnchor,
  posts,
  onOpen,
}: {
  anchor: Date;
  onChangeAnchor: (d: Date) => void;
  posts: ScheduledPost[];
  onOpen: (p: ScheduledPost) => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const weekStart = addDays(anchor, -anchor.getDay());
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = startOfDay(new Date());
  // 7 AM → 8 PM. Wider than business hours so a 9 PM scheduled clip
  // still shows up; tighter than 24h so the grid stays scannable.
  const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);

  const postsByDayHour = (d: Date, h: number): ScheduledPost[] =>
    posts.filter((p) => {
      const t = eventTime(p);
      return t && isSameDay(t, d) && t.getHours() === h;
    });

  const goPrev = (): void => onChangeAnchor(addDays(weekStart, -7));
  const goNext = (): void => onChangeAnchor(addDays(weekStart, 7));
  const goToday = (): void => onChangeAnchor(startOfDay(new Date()));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <button type="button" aria-label="Previous week" onClick={goPrev} style={iconBtn}>
          <ChevronLeft size={15} strokeWidth={2} />
        </button>
        <button type="button" aria-label="Next week" onClick={goNext} style={iconBtn}>
          <ChevronRight size={15} strokeWidth={2} />
        </button>
        <div style={{ fontSize: isMobile ? 14 : 18, fontWeight: 700, letterSpacing: "-0.01em", padding: "0 6px", flex: isMobile ? "1 1 auto" : "unset" }}>
          {weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
          {" – "}
          {addDays(weekStart, 6).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </div>
        <button
          type="button"
          onClick={goToday}
          style={{
            ...iconBtn,
            width: "auto",
            padding: "0 14px",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            background: "rgba(255,213,0,.08)",
            borderColor: "rgba(255,213,0,.25)",
          }}
        >
          Today
        </button>
      </div>

      <div
        style={{
          borderRadius: 18,
          overflow: "hidden",
          background: "rgba(16,18,27,.5)",
          border: "1px solid rgba(255,255,255,.05)",
          backdropFilter: "blur(20px)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "56px repeat(7, minmax(0,1fr))",
            padding: "10px 0 12px",
            borderBottom: "1px solid rgba(255,255,255,.05)",
          }}
        >
          <div />
          {days.map((d) => {
            const isTodayCol = isSameDay(d, today);
            return (
              <div key={d.toDateString()} style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: isTodayCol ? "var(--ql-yellow)" : "var(--ql-fg-subtle)",
                    textTransform: "uppercase",
                    letterSpacing: ".14em",
                    marginBottom: 6,
                  }}
                >
                  {d.toLocaleDateString(undefined, { weekday: "short" })}
                </div>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    margin: "0 auto",
                    borderRadius: 999,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isTodayCol ? "var(--ql-yellow)" : "transparent",
                    color: isTodayCol ? "#000" : "var(--ql-fg)",
                    fontWeight: 800,
                    fontSize: 14,
                    fontFamily: "var(--ql-font-mono)",
                  }}
                >
                  {d.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "56px repeat(7, minmax(0,1fr))",
            maxHeight: 620,
            overflowY: "auto",
          }}
        >
          {HOURS.map((h) => {
            const hLabel = h === 12 ? "12 PM" : h > 12 ? `${h - 12} PM` : `${h} AM`;
            return (
              // eslint-disable-next-line react/jsx-key
              <WeekHourRow
                key={h}
                hourLabel={hLabel}
                days={days}
                posts={posts}
                hour={h}
                onOpen={onOpen}
                postsByDayHour={postsByDayHour}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function WeekHourRow({
  hourLabel,
  days,
  hour,
  onOpen,
  postsByDayHour,
}: {
  hourLabel: string;
  days: Date[];
  posts: ScheduledPost[];
  hour: number;
  onOpen: (p: ScheduledPost) => void;
  postsByDayHour: (d: Date, h: number) => ScheduledPost[];
}): React.JSX.Element {
  return (
    <>
      <div
        style={{
          padding: "8px 10px 0",
          fontSize: 10,
          color: "var(--ql-fg-subtle)",
          fontFamily: "var(--ql-font-mono)",
          textAlign: "right",
          borderTop: "1px solid rgba(255,255,255,.04)",
        }}
      >
        {hourLabel}
      </div>
      {days.map((d) => {
        const slot = postsByDayHour(d, hour);
        return (
          <div
            key={d.toDateString()}
            style={{
              minHeight: 56,
              padding: 3,
              borderTop: "1px solid rgba(255,255,255,.04)",
              borderLeft: "1px solid rgba(255,255,255,.04)",
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minWidth: 0,
            }}
          >
            {slot.map((p) => (
              <WeekChip key={p.id} post={p} onOpen={onOpen} />
            ))}
          </div>
        );
      })}
    </>
  );
}

function WeekChip({
  post,
  onOpen,
}: {
  post: ScheduledPost;
  onOpen: (p: ScheduledPost) => void;
}): React.JSX.Element {
  const when = eventTime(post);
  const isFailed = post.status === "failed";
  const isPub = post.status === "published";
  const bar = isFailed ? "#F87171" : isPub ? "#4ADE80" : "var(--ql-yellow)";
  const bg = isFailed
    ? "rgba(248,113,113,.12)"
    : isPub
      ? "rgba(74,222,128,.10)"
      : "rgba(255,213,0,.10)";
  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      title={`${serviceLabel(post.service)} · ${when ? fmtTime(when) : "—"}\n\n${post.text}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "4px 6px",
        borderRadius: 6,
        border: "none",
        background: bg,
        borderLeft: `2px solid ${bar}`,
        cursor: "pointer",
        textAlign: "left",
        overflow: "hidden",
        minWidth: 0,
        fontFamily: "inherit",
      }}
    >
      <ServiceIcon service={post.service} size={14} />
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ql-fg)",
          fontFamily: "var(--ql-font-mono)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {when ? fmtTime(when).replace(/ AM| PM/, "") : "—"}
        {" · "}
        {(post.text.split("\n")[0] ?? "").slice(0, 22)}
      </span>
    </button>
  );
}

// ---------- month view -----------------------------------------------------

/** How many chips a cell shows before collapsing to "+N more". Buffer
 * uses 3, which works at our column widths too. */
const CHIPS_PER_CELL_COLLAPSED = 4;

function MonthView({
  anchor,
  onChangeAnchor,
  posts,
  onOpen,
  onReschedule,
  rescheduling,
}: {
  anchor: Date;
  onChangeAnchor: (d: Date) => void;
  posts: ScheduledPost[];
  onOpen: (p: ScheduledPost) => void;
  /** Drag-drop reschedule. The chip on day X is dropped onto day Y;
   *  the handler keeps the time-of-day and changes only the date. */
  onReschedule: (postId: string, targetDate: Date) => void;
  /** Post IDs currently being saved — we dim those chips and ignore
   *  re-drags until the round-trip resolves. */
  rescheduling: Set<string>;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  // Drag DOM events fire on children too — we track depth so the cell
  // doesn't briefly flicker un-highlighted when the pointer enters a
  // descendant.
  const dragDepthRef = useRef<Map<string, number>>(new Map());
  // Build a 6-week grid starting on Sunday containing the anchor month.
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = startOfDay(new Date());

  // Index posts by toDateString() — same shape as eventTime's day key.
  const byDay = useMemo(() => {
    const m = new Map<string, ScheduledPost[]>();
    for (const p of posts) {
      const t = eventTime(p);
      if (!t) continue;
      const key = startOfDay(t).toDateString();
      const arr = m.get(key) ?? [];
      arr.push(p);
      m.set(key, arr);
    }
    // Each day's posts ascending by time so the chips read top→bottom.
    for (const arr of m.values()) {
      arr.sort((a, b) => (eventTime(a)?.getTime() ?? 0) - (eventTime(b)?.getTime() ?? 0));
    }
    return m;
  }, [posts]);

  // Per-day expand state. Buffer's "+N more"/"Show less" is per-cell —
  // we mirror that so the user can pop one busy day open without losing
  // the rest of the grid to scrolling.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (key: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const goPrev = (): void =>
    onChangeAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
  const goNext = (): void =>
    onChangeAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
  const goToday = (): void => onChangeAnchor(startOfDay(new Date()));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, flexWrap: isMobile ? "wrap" : "nowrap" }}>
        <button type="button" aria-label="Previous month" onClick={goPrev} style={iconBtn}>
          <ChevronLeft size={15} strokeWidth={2} />
        </button>
        <button type="button" aria-label="Next month" onClick={goNext} style={iconBtn}>
          <ChevronRight size={15} strokeWidth={2} />
        </button>
        <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 800, letterSpacing: "-0.03em", padding: "0 6px", flex: isMobile ? "1 1 auto" : "unset" }}>
          {fmtMonthYear(anchor)}
        </div>
        <button
          type="button"
          onClick={goToday}
          style={{
            ...iconBtn,
            width: "auto",
            padding: "0 14px",
            fontSize: 12,
            fontWeight: 700,
            color: "var(--ql-yellow)",
            background: "rgba(255,213,0,.08)",
            borderColor: "rgba(255,213,0,.25)",
          }}
        >
          Today
        </button>
      </div>

      <div
        style={{
          borderRadius: 18,
          overflow: "hidden",
          background: "rgba(16,18,27,.5)",
          border: "1px solid rgba(255,255,255,.05)",
          backdropFilter: "blur(20px)",
          fontFeatureSettings: '"tnum"',
        }}
      >
        <div
          style={{
            display: "grid",
            // minmax(0, 1fr) is load-bearing — `1fr` alone resolves to
            // minmax(auto, 1fr), so long content (a chip with nowrap) blows
            // the column open. Without this, the month grid de-squares.
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            padding: "12px 0",
            borderBottom: "1px solid rgba(255,255,255,.05)",
          }}
        >
          {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map(
            (d) => (
              <div
                key={d}
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--ql-fg-subtle)",
                  textAlign: "center",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {d.slice(0, 3)}
              </div>
            ),
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
          }}
        >
          {days.map((d, idx) => {
            const key = d.toDateString();
            const isCurrentMonth = d.getMonth() === anchor.getMonth();
            const isTodayCell = isSameDay(d, today);
            const dayPosts = byDay.get(key) ?? [];
            const isExpanded = expanded.has(key);
            const visible = isExpanded
              ? dayPosts
              : dayPosts.slice(0, CHIPS_PER_CELL_COLLAPSED);
            const overflow = Math.max(0, dayPosts.length - CHIPS_PER_CELL_COLLAPSED);
            const isDropTarget = dragOverKey === key;
            return (
              <div
                key={key}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  const depth = (dragDepthRef.current.get(key) ?? 0) + 1;
                  dragDepthRef.current.set(key, depth);
                  if (depth === 1) setDragOverKey(key);
                }}
                onDragLeave={() => {
                  const depth = (dragDepthRef.current.get(key) ?? 1) - 1;
                  if (depth <= 0) {
                    dragDepthRef.current.delete(key);
                    setDragOverKey((k) => (k === key ? null : k));
                  } else {
                    dragDepthRef.current.set(key, depth);
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  dragDepthRef.current.delete(key);
                  setDragOverKey(null);
                  const postId = e.dataTransfer.getData("text/x-scheduled-post-id");
                  if (postId) onReschedule(postId, d);
                }}
                style={{
                  minHeight: 150,
                  padding: "10px 10px 12px",
                  background: isDropTarget ? "rgba(255,213,0,.10)" : "transparent",
                  outline: isDropTarget ? "1px dashed rgba(255,213,0,.50)" : "none",
                  outlineOffset: -1,
                  borderTop: idx >= 7 ? "1px solid rgba(255,255,255,.04)" : "none",
                  borderLeft: idx % 7 !== 0 ? "1px solid rgba(255,255,255,.04)" : "none",
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minWidth: 0,
                  opacity: isCurrentMonth ? 1 : 0.4,
                  transition: "background .12s var(--ql-ease)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 28,
                      height: 28,
                      padding: "0 7px",
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: "var(--ql-font-mono)",
                      borderRadius: 999,
                      letterSpacing: "-0.02em",
                      color: isTodayCell ? "#0B0C12" : "#D1D5DB",
                      background: isTodayCell ? "var(--ql-yellow)" : "transparent",
                    }}
                  >
                    {d.getDate()}
                  </div>
                  {dayPosts.length > 0 && (
                    <span
                      style={{
                        fontSize: 10,
                        color: "var(--ql-fg-subtle)",
                        fontFamily: "var(--ql-font-mono)",
                        fontWeight: 700,
                      }}
                    >
                      {dayPosts.length}
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    minWidth: 0,
                  }}
                >
                  {visible.map((p) => (
                    <MonthChip
                      key={p.id}
                      post={p}
                      onOpen={onOpen}
                      isBusy={rescheduling.has(p.id)}
                    />
                  ))}
                  {overflow > 0 && (
                    <button
                      type="button"
                      onClick={() => toggleExpand(key)}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 4px",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "var(--ql-fg-muted)",
                        background: "transparent",
                        border: "none",
                        borderRadius: 5,
                        cursor: "pointer",
                        textAlign: "left",
                        fontFamily: "inherit",
                        width: "fit-content",
                      }}
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp size={11} strokeWidth={2.4} />
                          Show less
                        </>
                      ) : (
                        <>
                          <ChevronDown size={11} strokeWidth={2.4} />
                          {overflow} more
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact post chip for the month grid. The brand glyph + compact time
 * carries the identity; the full caption lives in the tooltip + drawer.
 * Failed posts get a red tint; published chips fade slightly so the eye
 * picks out what's still scheduled.
 */
function MonthChip({
  post,
  onOpen,
  isBusy = false,
}: {
  post: ScheduledPost;
  onOpen: (p: ScheduledPost) => void;
  isBusy?: boolean;
}): React.JSX.Element {
  const when = eventTime(post);
  const isFailed = post.status === "failed";
  const isPub = post.status === "published";
  // Only "scheduled" posts can be re-timed via the provider. Once
  // a post is sent / failed / sending it's frozen — make the chip
  // non-draggable so the cursor never invites a forbidden action.
  const isDraggable = post.status === "scheduled" && !isBusy;
  const tooltip = [
    serviceLabel(post.service),
    post.channelName,
    when ? `${fmtDateLong(when)} · ${fmtTime(when)}` : "",
    `Status: ${STATUS_META[post.status].label}`,
    isDraggable ? "Drag to another day to reschedule" : "",
    "",
    post.text || "(no caption)",
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      title={tooltip}
      aria-label={`${serviceLabel(post.service)} ${when ? fmtTime(when) : ""}`}
      draggable={isDraggable}
      onDragStart={(e) => {
        if (!isDraggable) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.setData("text/x-scheduled-post-id", post.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 6px 4px 4px",
        borderRadius: 7,
        border: "none",
        background: isFailed ? "rgba(248,113,113,.10)" : "rgba(255,255,255,.04)",
        cursor: isBusy ? "wait" : isDraggable ? "grab" : "pointer",
        opacity: isBusy ? 0.55 : isPub ? 0.75 : 1,
        textAlign: "left",
        overflow: "hidden",
        minWidth: 0,
        width: "100%",
        fontFamily: "inherit",
        transition: "all .15s var(--ql-ease)",
      }}
      onMouseEnter={(e) => {
        if (isBusy) return;
        e.currentTarget.style.background = isFailed
          ? "rgba(248,113,113,.18)"
          : "rgba(255,255,255,.10)";
      }}
      onMouseLeave={(e) => {
        if (isBusy) return;
        e.currentTarget.style.background = isFailed
          ? "rgba(248,113,113,.10)"
          : "rgba(255,255,255,.04)";
      }}
    >
      <ServiceIcon service={post.service} size={16} />
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: isFailed ? "#FCA5A5" : "#D1D5DB",
          fontFamily: "var(--ql-font-mono)",
          flexShrink: 0,
        }}
      >
        {when ? fmtCompactTime(when) : "—"}
      </span>
    </button>
  );
}

// ---------- post drawer ----------------------------------------------------

function PostDrawer({
  post,
  batch,
  deleting,
  onDelete,
  onClose,
  onReschedule,
}: {
  post: ScheduledPost;
  /** Posts scheduled together with `post` (same video, same instant),
   *  including `post` itself. Powers "Delete all N". Length 1 = solo post. */
  batch: ScheduledPost[];
  /** A DELETE for this post is already in flight — disables the buttons. */
  deleting: boolean;
  /** Delete the given post ids from Buffer + the calendar, then close. */
  onDelete: (ids: string[]) => Promise<void>;
  onClose: () => void;
  /** Reschedule this post to an exact new instant (date + time). Resolves on
   *  success (the drawer then closes) or rejects with the provider error. */
  onReschedule: (postId: string, iso: string) => Promise<void>;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  // Two-step delete: first click reveals the confirm block (single vs batch),
  // second click commits. Never a one-click destructive action.
  const [confirming, setConfirming] = useState(false);
  // The inline "change date & time" editor, opened from the Reschedule button.
  const [editingTime, setEditingTime] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const when = eventTime(post);
  const meta = STATUS_META[post.status];
  const StatusIcon =
    post.status === "published" ? CheckCircle2 : post.status === "failed" ? AlertCircle : Clock;
  const canReschedule = post.status === "scheduled";
  // Only queued or failed posts can be pulled from the provider — a published
  // post is already out and Buffer won't delete it from the queue.
  const canDelete = post.status === "scheduled" || post.status === "failed";
  const batchCount = batch.length;
  const hasBatch = batchCount > 1;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        display: "flex",
        justifyContent: "flex-end",
        zIndex: 50,
        backdropFilter: "blur(8px)",
      }}
    >
      <aside
        style={{
          width: isMobile ? "100%" : "min(480px, 100%)",
          height: "100%",
          background: "rgba(10,12,18,.96)",
          borderLeft: "1px solid rgba(255,255,255,.08)",
          padding: isMobile ? "18px 16px" : "26px 28px",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 22,
          animation: "ql-slide-in .18s var(--ql-ease)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <ServiceIcon service={post.service} size={36} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>{serviceLabel(post.service)}</div>
            <div
              style={{
                fontSize: 12,
                color: "var(--ql-fg-muted)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {post.channelName}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ ...miniNavBtn, width: 32, height: 32, marginLeft: 0 }}
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>

        <PostMedia post={post} />

        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 4 }}>
            {when ? fmtDateLong(when) : "—"}
          </div>
          <div
            style={{
              fontSize: 14,
              color: "var(--ql-fg-muted)",
              fontFamily: "var(--ql-font-mono)",
              marginBottom: 14,
            }}
          >
            {when ? fmtTime(when) : "—"}
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "5px 12px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: meta.color,
              background: meta.bg,
              border: `1px solid ${meta.border}`,
            }}
          >
            <StatusIcon size={11} strokeWidth={2.4} />
            {meta.label}
          </span>
        </div>

        {editingTime && canReschedule && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              border: "1px solid rgba(255,213,0,.22)",
              background: "rgba(255,213,0,.04)",
              display: "grid",
              gap: 8,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ql-fg)" }}>Change date &amp; time</div>
            <RescheduleEditor
              currentISO={post.dueAt ?? new Date().toISOString()}
              onSave={async (iso) => {
                await onReschedule(post.id, iso);
                setEditingTime(false);
                onClose();
              }}
              onCancel={() => setEditingTime(false)}
              saveLabel="Save time"
              note={
                hasBatch
                  ? `This channel only — the other ${batchCount - 1} channel${
                      batchCount - 1 === 1 ? "" : "s"
                    } of this video keep their time (use the video library to move them together).`
                  : undefined
              }
            />
          </div>
        )}

        {post.errorMessage && (
          <div
            style={{
              padding: "12px 14px",
              borderRadius: 12,
              background: "rgba(248,113,113,.08)",
              border: "1px solid rgba(248,113,113,.25)",
              color: "#FCA5A5",
              fontSize: 12,
              lineHeight: 1.55,
            }}
          >
            <strong style={{ fontWeight: 700 }}>Provider error · </strong>
            {post.errorMessage}
          </div>
        )}

        <div>
          <div style={{ ...miniLabel, marginBottom: 8 }}>Caption</div>
          <div
            dir="auto"
            style={{
              fontSize: 14,
              lineHeight: 1.65,
              color: "var(--ql-fg)",
              whiteSpace: "pre-wrap",
              padding: "14px 16px",
              borderRadius: 12,
              background: "rgba(255,255,255,.03)",
              border: "1px solid rgba(255,255,255,.05)",
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {post.text || <em style={{ color: "var(--ql-fg-subtle)" }}>(no caption)</em>}
          </div>
        </div>

        <div style={{ ...miniLabel, fontSize: 10 }}>ID</div>
        <div
          style={{
            fontSize: 11,
            color: "var(--ql-fg-subtle)",
            fontFamily: "var(--ql-font-mono)",
            wordBreak: "break-all",
            marginTop: -14,
          }}
        >
          {post.id}
        </div>

        <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
        {canDelete && confirming && (
          <div
            style={{
              padding: "14px 16px",
              borderRadius: 12,
              background: "rgba(248,113,113,.06)",
              border: "1px solid rgba(248,113,113,.22)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: "#FCA5A5" }}>
              {hasBatch ? (
                <>
                  This post is <strong>1 of {batchCount}</strong> scheduled for this video at{" "}
                  <strong>{when ? `${fmtDateLong(when)}, ${fmtTime(when)}` : "this time"}</strong>.
                  Deleting removes it from the scheduler (Buffer) too — this can’t be undone.
                </>
              ) : (
                <>
                  Delete this post? It’ll be removed from the scheduler (<strong>Buffer</strong>)
                  too — this can’t be undone.
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => void onDelete([post.id])}
                disabled={deleting}
                style={{
                  flex: hasBatch ? "1 1 auto" : 1,
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid rgba(248,113,113,.35)",
                  background: "rgba(248,113,113,.14)",
                  color: "#FCA5A5",
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: deleting ? "default" : "pointer",
                  opacity: deleting ? 0.6 : 1,
                  fontFamily: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                }}
              >
                {deleting ? <Loader2 size={14} className="ql-spin" /> : <Trash2 size={14} strokeWidth={2} />}
                {hasBatch ? "Delete just this one" : "Delete"}
              </button>
              {hasBatch && (
                <button
                  type="button"
                  onClick={() => void onDelete(batch.map((b) => b.id))}
                  disabled={deleting}
                  style={{
                    flex: "1 1 auto",
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "none",
                    background: "linear-gradient(135deg,#F87171,#DC2626)",
                    color: "#fff",
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: deleting ? "default" : "pointer",
                    opacity: deleting ? 0.6 : 1,
                    fontFamily: "inherit",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 7,
                  }}
                >
                  {deleting ? <Loader2 size={14} className="ql-spin" /> : <Trash2 size={14} strokeWidth={2} />}
                  Delete all {batchCount}
                </button>
              )}
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={deleting}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,.10)",
                  background: "transparent",
                  color: "var(--ql-fg-muted)",
                  fontSize: 12.5,
                  fontWeight: 600,
                  cursor: deleting ? "default" : "pointer",
                  fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          {post.providerUrl ? (
            <a
              href={post.providerUrl}
              target="_blank"
              rel="noreferrer"
              style={{
                flex: 1,
                padding: "12px 18px",
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                fontFamily: "inherit",
                background: "linear-gradient(135deg,#FFD500,#FFA500)",
                color: "#000",
                fontSize: 13,
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                boxShadow: "0 4px 16px rgba(255,213,0,.25)",
                textDecoration: "none",
              }}
            >
              <ExternalLink size={14} strokeWidth={2} /> Open in scheduler
            </a>
          ) : (
            <div
              style={{
                flex: 1,
                padding: "12px 18px",
                borderRadius: 12,
                border: "1px dashed rgba(255,255,255,.10)",
                color: "var(--ql-fg-subtle)",
                fontSize: 12,
                textAlign: "center",
                fontFamily: "inherit",
              }}
            >
              No scheduler URL on this post yet
            </div>
          )}
          {canReschedule && (
            <button
              type="button"
              onClick={() => setEditingTime((v) => !v)}
              title="Change the date & time"
              aria-label="Change the date and time"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                border: editingTime ? "1px solid rgba(255,213,0,.5)" : "1px solid rgba(255,255,255,.08)",
                background: editingTime ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
                color: editingTime ? "var(--ql-yellow, #ffd500)" : "var(--ql-fg-muted)",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "inherit",
              }}
            >
              <CalendarClock size={16} strokeWidth={1.85} />
            </button>
          )}
          {canDelete && !confirming && (
            <button
              type="button"
              onClick={() => setConfirming(true)}
              title="Delete this post from the scheduler"
              aria-label="Delete post"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                border: "1px solid rgba(248,113,113,.20)",
                background: "rgba(248,113,113,.06)",
                color: "#F87171",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "inherit",
              }}
            >
              <Trash2 size={16} strokeWidth={1.85} />
            </button>
          )}
        </div>
        </div>
      </aside>
    </div>
  );
}

// ---------- shared bits ----------------------------------------------------

/**
 * Drawer media block. Plays the actual video when one is attached
 * (catalog video clips ship with `videoUrl` pointing at our R2
 * public bucket), falls back to the thumbnail image otherwise, and
 * ultimately to the brand-tinted gradient if no media is present.
 *
 * Aspect ratio is media-type-aware:
 *   - Video posts (catalog clips)  → 9:16 (our render shape)
 *   - Image-only posts (Beta carousel slides — Instagram post format)
 *                                  → 4:5 (Instagram portrait)
 *
 * Operator reported on 2026-05-26 that image-only posts were being
 * letterboxed into a 9:16 frame in the calendar drawer, which hid
 * the post's native shape and felt wrong. Branching on
 * `post.videoUrl` presence picks the right shape per item.
 *
 * `preload="metadata"` keeps the duration in controls visible
 * without buffering the whole file.
 */
function PostMedia({ post }: { post: ScheduledPost }): React.JSX.Element {
  const [videoBroken, setVideoBroken] = useState(false);
  const showVideo = !!post.videoUrl && !videoBroken;
  const aspect = post.videoUrl ? "9 / 16" : "4 / 5";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
      <div
        style={{
          width: 220,
          aspectRatio: aspect,
          borderRadius: 16,
          overflow: "hidden",
          background: post.imageUrl
            ? `center/cover no-repeat url("${post.imageUrl}")`
            : `linear-gradient(135deg, ${serviceBrandBg(post.service)}, rgba(0,0,0,.4))`,
          boxShadow: "0 20px 60px rgba(0,0,0,.5), inset 0 0 0 1px rgba(255,255,255,.06)",
          position: "relative",
        }}
      >
        {showVideo ? (
          <video
            src={post.videoUrl}
            controls
            playsInline
            preload="metadata"
            poster={post.imageUrl}
            onError={() => setVideoBroken(true)}
            style={{
              width: "100%",
              height: "100%",
              display: "block",
              background: "#000",
              objectFit: "cover",
            }}
          />
        ) : !post.imageUrl ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,213,0,.40)",
            }}
          >
            <ImageIcon size={36} strokeWidth={1.5} />
          </div>
        ) : null}
        {!showVideo && post.text && (
          <div
            style={{
              position: "absolute",
              left: 14,
              right: 14,
              bottom: 18,
              textAlign: "center",
              fontSize: 11,
              fontWeight: 800,
              color: "#fff",
              textShadow: "0 1px 4px rgba(0,0,0,.8)",
              lineHeight: 1.3,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {post.text.split("\n")[0]}
          </div>
        )}
      </div>

      {post.videoUrl && (
        <a
          href={post.videoUrl}
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 11,
            color: "var(--ql-fg-subtle)",
            textDecoration: "none",
            fontFamily: "var(--ql-font-mono)",
          }}
          title="Open the source mp4 in a new tab"
        >
          <ExternalLink size={11} strokeWidth={2} />
          Open mp4 in new tab
        </a>
      )}
    </div>
  );
}

// ---------- grouping helper -----------------------------------------------

function groupByDay(posts: ScheduledPost[]): Array<{
  dateKey: string;
  date: Date | null;
  posts: ScheduledPost[];
}> {
  const map = new Map<string, { date: Date | null; posts: ScheduledPost[] }>();
  for (const p of posts) {
    const t = eventTime(p);
    const key = t ? t.toDateString() : "undated";
    let bucket = map.get(key);
    if (!bucket) {
      bucket = { date: t ? startOfDay(t) : null, posts: [] };
      map.set(key, bucket);
    }
    bucket.posts.push(p);
  }
  const groups = Array.from(map.entries()).map(([dateKey, v]) => ({
    dateKey,
    date: v.date,
    posts: v.posts.sort((a, b) => {
      const ta = eventTime(a)?.getTime() ?? 0;
      const tb = eventTime(b)?.getTime() ?? 0;
      return ta - tb;
    }),
  }));
  // Sort: today + future ascending first, then past descending, undated last.
  const today = startOfDay(new Date()).getTime();
  groups.sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    const ta = a.date.getTime();
    const tb = b.date.getTime();
    const aFuture = ta >= today;
    const bFuture = tb >= today;
    if (aFuture && bFuture) return ta - tb;
    if (!aFuture && !bFuture) return tb - ta;
    return aFuture ? -1 : 1;
  });
  return groups;
}

// ---------- empty / skeleton ----------------------------------------------

function EmptyHero({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}): React.JSX.Element {
  return (
    <div
      style={{
        padding: "60px 24px",
        textAlign: "center",
        border: "1px dashed rgba(255,255,255,.08)",
        borderRadius: 16,
        background: "rgba(16,18,27,.40)",
        color: "var(--ql-fg-muted)",
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 16,
          margin: "0 auto 14px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(255,213,0,.10)",
          color: "var(--ql-yellow)",
        }}
      >
        {icon}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ql-fg)", marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ fontSize: 13, maxWidth: 420, margin: "0 auto", lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

function SkeletonRows(): React.JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          style={{
            height: 86,
            borderRadius: 14,
            background:
              "linear-gradient(90deg, rgba(255,255,255,.025) 0%, rgba(255,255,255,.05) 50%, rgba(255,255,255,.025) 100%)",
            backgroundSize: "200% 100%",
            animation: "ql-shimmer 1.4s linear infinite",
          }}
        />
      ))}
    </div>
  );
}
